import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { WatchList } from '~/shared/data-contract/watch';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';
import { optimisticallyWatchItem, restoreWatches } from './watch-cache';

type WatchMutationContext = {
    readonly previous: WatchList;
};

export type UseWatchItemResult = UseMutationResult<unknown, Error, string, WatchMutationContext>;

export const useWatchItem = (): UseWatchItemResult => {
    const { watchItem } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation<unknown, Error, string, WatchMutationContext>({
        mutationFn: async (itemId: string) => {
            const result = await watchItem(itemId);

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onError: (_error, _itemId, context) => {
            if (context !== undefined && context !== null) {
                restoreWatches(queryClient, context.previous);
            }
        },
        onMutate: async (itemId) => {
            await queryClient.cancelQueries({ queryKey: jobArchitectureKeys.watches() });

            return { previous: optimisticallyWatchItem(queryClient, itemId) };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.watches() });
        },
        throwOnError: false,
    });
};
